import L, { LatLng, PolylineOptions } from "leaflet";
import { useEffect, useRef, useState } from "react";
import { Polygon, useMapEvents } from "react-leaflet";
import { BLUE_HIGHLIGHT } from "../../constants/colors";
import { enforceMinRectangleSize } from "../../utils/shape/enforceMinRectangleSize";
import Bounds from "./Bounds";

interface ResizeAndTranslateToolProps {
  id: string;
  geometry: [number, number][] | LatLng[]; // Changed to a 2D array of numbers
  color?: string;
  onTransform?: (newGeometry: [number, number][] | LatLng[]) => void;
  rotation: boolean;
  uniformScaling: boolean;
}

const ResizeAndTranslateTool = ({
  id,
  geometry,
  onTransform,
  uniformScaling,
  rotation,
}: ResizeAndTranslateToolProps) => {
  const polygonRef = useRef<L.Polygon | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isScaling, setIsScaling] = useState(false);
  const [fallbackGeometry, setFallbackGeometry] = useState<LatLng[] | null>(null);

  useEffect(() => {
    if (!polygonRef.current || !polygonRef.current.transform) return;
    const layer = polygonRef.current; // Access the Leaflet layer

    // Disabled by default to prevent unwanted transformations prevent ophaned transform layers
    layer.transform.disable();

    // As this is a leaflet plugin which is not comptible with React, we need to update the transform manually

    layer.transform.setOptions({
      uniformScaling: uniformScaling,
      rotation: rotation,
      scaling: true,
      handlerOptions: {
        radius: 6,
        fillColor: "#ffffff",
        fillOpacity: 1,
        color: "#3388ff",
        weight: 2,
        opacity: 0.7,
        setCursor: true,
        renderer: L.svg(),
      },
      boundsOptions: {
        weight: 2,
        opacity: 1,
        dashArray: [6, 6],
        fill: false,
        noClip: true,
        renderer: L.svg(),
        color: BLUE_HIGHLIGHT,
      } as PolylineOptions,
      rotateHandleOptions: {
        weight: 1,
        opacity: 1,
        setCursor: true,
        renderer: L.svg(),
      } as PolylineOptions,
    });

    layer.transform.enable();
    return () => {
      // Cleanup transform on dismount
      layer.transform.disable();
    };
  }, [geometry, uniformScaling, rotation]);

  const revertGeometryIfInvalid = (): boolean => {
    if (fallbackGeometry) {
      const polygon = polygonRef.current;
      const positions = polygonRef.current?.getLatLngs() as LatLng[][];
      if (!!polygon && !!positions && positions.length >= 1 && positions[0].length >= 4) {
        const startBounds = new Bounds(fallbackGeometry);
        const endBounds = new Bounds(positions[0]);
        if (!endBounds.isSame(startBounds)) {
          if (endBounds.width() < 1 || endBounds.height() < 1) {
            positions[0] = fallbackGeometry;
            // Make a tiny change to force Leaflet to recognize the geometry has changed
            positions[0][0].lat += 0.000000001;

            polygon.fire("transformed", {
              target: polygon,
              type: "transformed",
            });
            return true;
          }
        }
      }
    }
    return false;
  };

  useMapEvents({
    mousedown(e) {
      const clickedElement = e.originalEvent.target as HTMLElement;
      const className =
        clickedElement instanceof SVGElement
          ? clickedElement.className.baseVal
          : clickedElement.className;
      if (className.includes("leaflet-drag-transform-marker")) {
        const polygon = polygonRef.current;
        const positions = polygonRef.current?.getLatLngs() as LatLng[][];
        if (!!polygon && !!positions && positions.length >= 1 && positions[0].length >= 4) {
          setFallbackGeometry(positions[0].map((g) => new L.LatLng(g.lat, g.lng)));
          setIsScaling(true);
        }
      }
    },
    mouseup(e) {
      if (isScaling && fallbackGeometry) {
        revertGeometryIfInvalid();
        setIsScaling(false);
      }
    },
    keydown(e) {
      // Ignore Escape key to prevent errors when cancelling drag
      if (isDragging && e.originalEvent.key === "Escape") {
        const polygon = polygonRef.current;
        if (polygon && polygon.dragging && (polygon.dragging as any)._enabled) {
          e.originalEvent.stopPropagation();
          e.originalEvent.preventDefault();
        }
      }
    },
  });

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      try {
        if (revertGeometryIfInvalid()) {
          setIsScaling(false);
        }
      } catch {}
    };

    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, [isScaling, fallbackGeometry]);

  const polygonKey = `${id}-${JSON.stringify(geometry)}`;

  return (
    <Polygon
      key={polygonKey}
      ref={polygonRef}
      transform={true}
      draggable={true}
      pathOptions={{
        weight: 2,
        dashArray: "6, 6",
        opacity: isDragging ? 1 : 0,
        color: BLUE_HIGHLIGHT,
        fillOpacity: 0,
      }}
      positions={geometry}
      eventHandlers={{
        transformed: (e: any) => {
          if (!onTransform) return;
          const newPositions = e.target.getLatLngs() as LatLng[][];
          const validatedPositions = enforceMinRectangleSize(newPositions[0]);
          onTransform(validatedPositions);
        },
        dragstart: () => {
          const polygon = polygonRef.current;
          if (polygon) {
            polygon.setStyle({
              opacity: 1,
            });
          }
          setIsDragging(true);
        },
        dragend: () => {
          const polygon = polygonRef.current;
          if (polygon) {
            polygon.setStyle({
              opacity: 0,
            });
          }
          setIsDragging(false);
        },
      }}
    />
  );
};

export default ResizeAndTranslateTool;
